<style>
@import '@/assets/css/custom.css';
</style>

<template>
	<div>
		<Breadcrumbs main="Accounts" title="Customer/Dealer Accounts" />

		<div class="container-fluid">
			<div class="row">
				<div class="col-sm-12">
					<div class="card">
						<div class="card-body">
							<div class="col-md-12 project-list filter-sort">
								<div class="row align-items-end">
									<div class="col-md-6 d-flex mt-2">
										<label class="d-flex listing-length ms-1 align-items-center">
											<span>Show</span>
											<select class="form-select mx-2" style="width: auto" v-model="listing_length" @change="fetchUserCredits()">
												<option value="5">5</option>
												<option value="10">10</option>
												<option value="20">20</option>
											</select>
											<span>entries</span>
										</label>
									</div>
									<div class="col-md-2 mt-2">
										<select class="form-select" v-model="user_type" @change="fetchUserCredits()">
											<option value="">All</option>
											<option value="1">Dealer</option>
											<option value="2">Customer</option>
										</select>
									</div>
									<div class="col-md-4 mt-2">
										<input
											type="text"
											class="form-control"
											placeholder="Search by name or mobile…"
											v-model="keyword"
											@keyup="fetchUserCredits()"
										/>
									</div>
								</div>
							</div>

							<div class="table-responsive product-table mt-3">
								<table class="display table align-middle">
									<thead>
										<tr align="center">
											<th>Sl.No</th>
											<th>User</th>
											<th>Type</th>
											<th>Mobile</th>
											<th>Due Amount</th>
											<th>Deposit</th>
											<th>Updated</th>
											<th>Action</th>
										</tr>
									</thead>
									<tbody v-if="rows.length">
										<tr align="center" v-for="(row, index) in rows" :key="row.id" class="align-middle">
											<td>{{ from != null ? from + index : index + 1 }}</td>
											<td>{{ row.user_name || row.dealer_name || row.customer_name }}</td>
											<td>
												<span class="badge rounded-pill bg-info text-dark" v-if="row.user_type == 1">Dealer</span>
												<span class="badge rounded-pill bg-secondary" v-else-if="row.user_type == 2">Customer</span>
												<span class="badge rounded-pill bg-light text-dark" v-else>—</span>
											</td>
											<td>{{ row.user_mobile || "—" }}</td>
											<td>₹{{ formatMoney(row.credit_due_amount) }}</td>
											<td class="fw-bold text-success">₹{{ formatMoney(row.credit_available_amount) }}</td>
											<td>{{ row.updated_at ? $filters.formatDate(row.updated_at) : "—" }}</td>
											<td>
												<div class="d-flex flex-column align-items-center justify-content-center gap-2">
													<router-link :to="paymentPageLink(row)" class="btn btn-success"> Pay / repay </router-link>
													<router-link :to="{ name: 'user_payment_logs', params: { id: row.user_id } }" class="btn btn-primary">
														View Payment Logs
													</router-link>
													<router-link :to="{ name: 'user_credit_logs', params: { id: row.user_id } }" class="btn btn-warning">
														View Credit Logs
													</router-link>
													<router-link :to="{ name: 'user_dues', params: { id: row.user_id } }" class="btn btn-danger">View Dues</router-link>
													<button type="button" class="btn btn-info" @click="openUpdateModal(row)">Update Due/Advance</button>
												</div>
											</td>
										</tr>
									</tbody>
									<tbody v-else>
										<tr>
											<td colspan="7" align="center">No user credits found</td>
										</tr>
									</tbody>
								</table>
							</div>

							<caption v-if="rows.length" class="float-start mt-2 d-block">
								Showing {{ from }} to {{ to }} of {{ total }} entries
							</caption>
							<nav v-if="rows.length && pagination.length" aria-label="Page navigation" class="mt-2 float-end">
								<ul class="pagination mb-0">
									<li
										class="page-item"
										v-bind:class="[{ disabled: !page.url }, { active: page.active }]"
										v-for="(page, index) in pagination"
										:key="'p-' + index + '-' + page.label"
									>
										<a class="page-link" href="#" @click.prevent="onPageClick(page, index)" tabindex="-1">
											<span v-if="index === 0">Prev</span>
											<span v-else-if="index === pagination.length - 1">Next</span>
											<span v-else>{{ page.label }}</span>
										</a>
									</li>
								</ul>
							</nav>
						</div>
					</div>
				</div>
			</div>
		</div>

		<DynamicModal :title="modal_title">
			<template #body>
				<form @submit.prevent="submitUpdateCredit">
					<div class="row">
						<div class="col-md-12 form-group mt-2">
							<label class="control-label">User</label>
							<input class="form-control" type="text" :value="updateForm.user_name" disabled />
						</div>
						<div class="col-md-12 form-group mt-2">
							<label class="control-label">Due Amount<span class="text-danger">*</span></label>
							<input
								class="form-control"
								type="number"
								min="0"
								step="0.01"
								v-model="updateForm.due"
								placeholder="Enter due amount"
								:class="{ 'is-invalid': errors.due }"
							/>
							<div v-if="errors.due" class="invalid-feedback d-block">{{ errors.due }}</div>
						</div>
						<div class="col-md-12 form-group mt-2">
							<label class="control-label">Deposit<span class="text-danger">*</span></label>
							<input
								class="form-control"
								type="number"
								min="0"
								step="0.01"
								v-model="updateForm.advance"
								placeholder="Enter deposit amount"
								:class="{ 'is-invalid': errors.advance }"
							/>
							<div v-if="errors.advance" class="invalid-feedback d-block">{{ errors.advance }}</div>
						</div>
					</div>

					<div class="col-md-12 form-group mt-3">
						<button type="submit" class="btn btn-primary me-2" :disabled="isSubmitting">
							<span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
							{{ isSubmitting ? "Updating..." : button_title }}
						</button>
						<button type="button" class="btn btn-light" @click="closeModal" :disabled="isSubmitting">Close</button>
					</div>
				</form>
			</template>
		</DynamicModal>
	</div>
</template>

<script>
import { onMounted } from "vue";
import { Modal } from "bootstrap";
import DynamicModal from "@/components/dynamic_modal.vue";

let dynamicmodal;

export default {
	name: "CustomerDealerAccounts",
	components: { DynamicModal },
	setup() {
		onMounted(() => {
			const el = document.getElementById("dynamicmodal");
			if (el) dynamicmodal = new Modal(el);
		});
		return {
			showModal: () => dynamicmodal && dynamicmodal.show(),
			hideModal: () => dynamicmodal && dynamicmodal.hide(),
		};
	},
	data() {
		return {
			token: "",
			keyword: "",
			listing_length: 10,
			rows: [],
			pagination: [],
			from: 0,
			to: 0,
			total: 0,
			active_page_url: "",
			user_type: "",
			modal_title: "Update Credit",
			button_title: "Update",
			isSubmitting: false,
			errors: {},
			updateForm: {
				user_id: null,
				user_name: "",
				due: "",
				advance: "",
			},
		};
	},
	created() {
		this.fetchUserCredits();
	},
	methods: {
		getToken() {
			if (localStorage.getItem("airotrack_staff")) {
				const airotrack_staff = JSON.parse(localStorage.getItem("airotrack_staff"));
				if (airotrack_staff.token) {
					this.token = airotrack_staff.token;
				} else {
					this.$swal("", "Failed to fetch token, please login again", "error");
					this.$router.push({ name: "login" });
				}
			} else {
				this.$swal("", "Session expired, please login again", "error");
				this.$router.push({ name: "login" });
			}
		},

		listUrl() {
			return `/staffs/userCredits?limit=${this.listing_length}&keyword=${encodeURIComponent(this.keyword || "")}&user_type=${this.user_type}`;
		},

		fetchUserCredits(pageUrl) {
			this.getToken();
			const url = pageUrl || this.listUrl();
			this.active_page_url = url;
			this.$axios
				.get(url, { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					if (res.data.status !== "true" && res.data.status !== true) {
						this.rows = [];
						this.pagination = [];
						return;
					}
					const paginator = res.data.data?.userCredits;
					this.rows = paginator?.data || [];
					this.pagination = paginator?.links || [];
					this.from = paginator?.from ?? 0;
					this.to = paginator?.to ?? 0;
					this.total = paginator?.total ?? 0;
				})
				.catch((err) => {
					console.error(err);
					this.$swal("Error", err.response?.data?.message || "Failed to load user credits", "error");
					this.rows = [];
					this.pagination = [];
				});
		},

		onPageClick(page) {
			if (!page.url) return;
			const base = page.url;
			const sep = base.includes("?") ? "&" : "?";
			this.fetchUserCredits(
				`${base}${sep}limit=${this.listing_length}&keyword=${encodeURIComponent(this.keyword || "")}&user_type=${this.user_type}`
			);
		},

		formatMoney(value) {
			const n = parseFloat(value);
			if (isNaN(n)) return "0.00";
			return n.toFixed(2);
		},

		paymentPageLink(row) {
			const query = {
				user_name: row.user_name || "",
				user_mobile: row.user_mobile || "",
			};
			if (row.user_type == 1 || row.user_type == 2) {
				query.payment_user_type = String(row.user_type);
			}
			return {
				name: "user_account_payment",
				params: { id: row.user_id },
				query,
			};
		},

		resetUpdateForm() {
			this.updateForm = {
				user_id: null,
				user_name: "",
				due: "",
				advance: "",
			};
			this.errors = {};
		},

		openUpdateModal(row) {
			this.resetUpdateForm();
			this.updateForm.user_id = row.user_id;
			this.updateForm.user_name = row.user_name || row.dealer_name || row.customer_name || "";
			this.updateForm.due =
				row.credit_due_amount != null && row.credit_due_amount !== "" ? row.credit_due_amount : "";
			this.updateForm.advance =
				row.credit_available_amount != null && row.credit_available_amount !== "" ? row.credit_available_amount : "";
			this.modal_title = "Update Credit";
			this.button_title = "Update";
			this.isSubmitting = false;
			this.showModal();
		},

		closeModal() {
			this.hideModal();
			this.resetUpdateForm();
			this.isSubmitting = false;
		},

		validateUpdateForm() {
			this.errors = {};
			const dueEmpty =
				this.updateForm.due === "" ||
				this.updateForm.due === null ||
				this.updateForm.due === undefined;
			const advanceEmpty =
				this.updateForm.advance === "" ||
				this.updateForm.advance === null ||
				this.updateForm.advance === undefined;

			if (dueEmpty) {
				this.errors.due = "Due amount is required";
			} else if (parseFloat(this.updateForm.due) < 0) {
				this.errors.due = "Due amount cannot be negative";
			}

			if (advanceEmpty) {
				this.errors.advance = "Deposit amount is required";
			} else if (parseFloat(this.updateForm.advance) < 0) {
				this.errors.advance = "Deposit amount cannot be negative";
			}

			return Object.keys(this.errors).length === 0;
		},

		submitUpdateCredit() {
			if (this.isSubmitting) return;
			if (!this.validateUpdateForm()) return;
			if (!this.updateForm.user_id) {
				this.$swal("Error", "User not found", "error");
				return;
			}

			this.isSubmitting = true;
			this.getToken();

			this.$axios
				.post(
					"/staffs/updateUserCredit",
					null,
					{
						headers: { Authorization: "Bearer " + this.token },
						params: {
							user_id: this.updateForm.user_id,
							advance: this.updateForm.advance,
							due: this.updateForm.due,
						},
					}
				)
				.then((res) => {
					if (res.data.status === true || res.data.status === "true") {
						this.$swal("Success", res.data.message || "Credit updated successfully", "success");
						this.closeModal();
						this.fetchUserCredits(this.active_page_url || this.listUrl());
					} else {
						this.$swal("Error", res.data.message || "Failed to update credit", "error");
					}
				})
				.catch((err) => {
					console.error(err);
					this.$swal("Error", err.response?.data?.message || "Failed to update credit", "error");
				})
				.finally(() => {
					this.isSubmitting = false;
				});
		},
	},
};
</script>

